import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { NotificationFullData } from "@/shared/types";
import { DateNTime } from "@/shared/utils/dateTime";
import { ROUTES } from "@/shared/constants";

import { useMarkAllNotificationAsRead } from "../api";
import {
  notificationPopoverPaperSx,
  notificationPopoverScrollSx,
} from "./notificationItemStyles";

type NotificationPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  isMobile: boolean;
  notifications?: NotificationFullData[];
  onMarkedAllRead?: () => void;
};

export function NotificationPopover({
  open,
  anchorEl,
  onClose,
  isMobile,
  notifications = [],
  onMarkedAllRead,
}: NotificationPopoverProps) {
  const { t } = useTranslation("notification");
  const navigate = useNavigate();
  const { currentProfile } = useCurrentTalentProfile();
  const queryClient = useQueryClient();
  const isTalent = Boolean(currentProfile?.id);

  const { update: markAllAsRead, isLoading: isMarkingAllRead } = useMarkAllNotificationAsRead({
    key: ["notification-popover-mark-all-read"],
  });

  const total = notifications.length;
  const unreadCount = notifications.filter((item) => !item.is_read).length;

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllAsRead({
        ...(isTalent && currentProfile?.id ? { talent_id: currentProfile.id } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ["notification"] });
      await queryClient.invalidateQueries({ queryKey: ["notification-page"] });
      onMarkedAllRead?.();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleItemClick = () => {
    onClose();
    navigate(ROUTES.NOTIFICATION);
  };

  return (
    <Popover
      id="notifications-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: notificationPopoverPaperSx(isMobile) }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "var(--primary-font-family)",
                fontWeight: 700,
                fontSize: "1.0625rem",
                color: "var(--primary-1000)",
                lineHeight: 1.2,
              }}
            >
              {t("notifications")}
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontFamily: "var(--primary-font-family)",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              <Box component="span" sx={{ color: "var(--primary-1000)" }}>
                {total} {t("total_label")}
              </Box>
              <Box component="span" sx={{ color: "var(--primary-700)" }}>
                {" "}
                • {unreadCount} {t("unread_label")}
              </Box>
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                disabled={isMarkingAllRead}
                onClick={handleMarkAllAsRead}
                sx={{
                  textTransform: "none",
                  fontFamily: "var(--primary-font-family)",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "var(--primary-700)",
                  minWidth: 0,
                  px: 0.75,
                  py: 0.25,
                  "&:hover": { bgcolor: "var(--primaryAlpha)" },
                }}
              >
                {isMarkingAllRead ? t("marking_all_read") : t("mark_all_read")}
              </Button>
            )}

            {isMobile && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: "var(--primary-700)" }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={notificationPopoverScrollSx}>
        {notifications.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              py: 4,
              fontFamily: "var(--primary-font-family)",
              fontSize: "0.8125rem",
              color: "var(--primary-800)",
            }}
          >
            {t("no-new-notifications")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {notifications.map((item, index) => (
              <PopoverItem
                key={item.id ?? index}
                item={item}
                onClick={handleItemClick}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid var(--primary-200)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Link
          to={ROUTES.NOTIFICATION}
          onClick={onClose}
          style={{ textDecoration: "none" }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              sx={{
                fontFamily: "var(--primary-font-family)",
                fontSize: "0.8125rem",
                fontWeight: 650,
                color: "var(--primary-700)",
              }}
            >
              {t("view_all_notifications")}
            </Typography>
            <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: "var(--primary-700)" }} />
          </Stack>
        </Link>
      </Box>
    </Popover>
  );
}

function PopoverItem({
  item,
  onClick,
}: {
  item: NotificationFullData;
  onClick: () => void;
}) {
  const { t } = useTranslation("notification");
  const isUnread = !item.is_read;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: "relative",
        px: 1.75,
        py: 1.5,
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: isUnread ? "var(--primary-100)" : "var(--primary-100-light)",
        border: isUnread ? "none" : "1px solid var(--primary-200)",
        boxShadow: isUnread
          ? "inset 4px 0 0 0 var(--primary-600), var(--shadow-xs)"
          : "var(--shadow-xs)",
        "&:hover": {
          bgcolor: isUnread ? "var(--primary-200)" : "var(--primary-100)",
        },
      }}
    >
      {isUnread && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            px: 0.9,
            py: 0.3,
            borderRadius: "999px",
            bgcolor: "var(--primary-600)",
            color: "var(--primary-bg-dark)",
            fontFamily: "var(--primary-font-family)",
            fontSize: "0.5625rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {t("new_tag")}
        </Box>
      )}

      <Typography
        sx={{
          fontFamily: "var(--primary-font-family)",
          fontWeight: 700,
          fontSize: "0.875rem",
          color: "var(--primary-1000)",
          pr: isUnread ? 5 : 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.title}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontFamily: "var(--primary-font-family)",
          fontSize: "0.75rem",
          color: "var(--primary-800)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.message}
      </Typography>

      {item.created_at && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: "var(--primary-font-family)",
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: isUnread ? "var(--primary-700)" : "var(--primary-800)",
          }}
        >
          {DateNTime.formatRelative(item.created_at)}
        </Typography>
      )}
    </Box>
  );
}
